import React, { useMemo, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IProjectPhase,
  IPhaseUpdateRequest,
  PHASE_TYPE_LABELS,
  PHASE_TYPE_COLORS,
  PHASE_STATUS_COLORS,
  PHASE_STATUS_LABELS,
} from '@/types/project';

interface PhaseTimelinePlannerProps {
  phases: IProjectPhase[];
  projectStartDate?: string;
  projectEndDate?: string;
  onUpdatePhase: (phaseId: string, data: IPhaseUpdateRequest) => void;
  isUpdating?: boolean;
}

const DAY_MS = 86400000;
const ROW_HEIGHT = 40;
const LABEL_WIDTH = 180;
const HANDLE_WIDTH = 8;
const MIN_BAR_DAYS = 1;

function parseDate(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function formatYMD(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function diffDays(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

const PhaseTimelinePlanner: React.FC<PhaseTimelinePlannerProps> = ({
  phases,
  projectStartDate,
  projectEndDate,
  onUpdatePhase,
  isUpdating,
}) => {
  const { t } = useTranslation('project');
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<{
    phaseId: string;
    handle: 'left' | 'right' | 'body';
    startX: number;
    origStart: Date;
    origEnd: Date;
  } | null>(null);
  const [dragPreview, setDragPreview] = useState<{ start: Date; end: Date } | null>(null);

  // Compute timeline range
  const { rangeStart, rangeEnd, totalDays } = useMemo(() => {
    const dates: Date[] = [];
    const projStart = parseDate(projectStartDate || '');
    const projEnd = parseDate(projectEndDate || '');
    if (projStart) dates.push(projStart);
    if (projEnd) dates.push(projEnd);

    phases.forEach((p) => {
      const s = parseDate(p.planned_start);
      const e = parseDate(p.planned_end);
      if (s) dates.push(s);
      if (e) dates.push(e);
    });

    if (dates.length === 0) {
      const now = new Date();
      return { rangeStart: now, rangeEnd: new Date(now.getTime() + 90 * DAY_MS), totalDays: 90 };
    }

    let min = dates[0];
    let max = dates[0];
    dates.forEach((d) => {
      if (d < min) min = d;
      if (d > max) max = d;
    });

    // Add padding
    const start = new Date(min.getTime() - 7 * DAY_MS);
    const end = new Date(max.getTime() + 14 * DAY_MS);
    return { rangeStart: start, rangeEnd: end, totalDays: Math.max(diffDays(start, end), 30) };
  }, [phases, projectStartDate, projectEndDate]);

  // Generate month headers
  const months = useMemo(() => {
    const result: { label: string; left: number; width: number }[] = [];
    const cursor = new Date(rangeStart);
    cursor.setDate(1);
    if (cursor < rangeStart) cursor.setMonth(cursor.getMonth() + 1);

    while (cursor <= rangeEnd) {
      const dayOffset = diffDays(rangeStart, cursor);
      const nextMonth = new Date(cursor);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const endOffset = Math.min(diffDays(rangeStart, nextMonth), totalDays);
      const label = cursor.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
      result.push({
        label,
        left: (dayOffset / totalDays) * 100,
        width: ((endOffset - dayOffset) / totalDays) * 100,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return result;
  }, [rangeStart, rangeEnd, totalDays]);

  // Today marker position
  const todayPos = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    if (now < rangeStart || now > rangeEnd) return null;
    return (diffDays(rangeStart, now) / totalDays) * 100;
  }, [rangeStart, rangeEnd, totalDays]);

  // Pixel width per day
  const pxPerDay = useMemo(() => {
    const el = containerRef.current;
    if (!el) return 8;
    return (el.clientWidth - LABEL_WIDTH) / totalDays;
  }, [totalDays]);

  const dayWidth = Math.max(pxPerDay, 4);
  const chartWidth = Math.max(totalDays * dayWidth, 600);

  // Handle drag
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, phaseId: string, handle: 'left' | 'right' | 'body', start: Date, end: Date) => {
      if (isUpdating) return;
      e.preventDefault();
      setDragState({ phaseId, handle, startX: e.clientX, origStart: start, origEnd: end });
      setDragPreview({ start, end });
    },
    [isUpdating]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragState) return;
      const dx = e.clientX - dragState.startX;
      const daysDelta = Math.round(dx / dayWidth);

      let newStart = dragState.origStart;
      let newEnd = dragState.origEnd;

      if (dragState.handle === 'left') {
        newStart = new Date(dragState.origStart.getTime() + daysDelta * DAY_MS);
        if (diffDays(newStart, newEnd) < MIN_BAR_DAYS) {
          newStart = new Date(newEnd.getTime() - MIN_BAR_DAYS * DAY_MS);
        }
      } else if (dragState.handle === 'right') {
        newEnd = new Date(dragState.origEnd.getTime() + daysDelta * DAY_MS);
        if (diffDays(newStart, newEnd) < MIN_BAR_DAYS) {
          newEnd = new Date(newStart.getTime() + MIN_BAR_DAYS * DAY_MS);
        }
      } else {
        newStart = new Date(dragState.origStart.getTime() + daysDelta * DAY_MS);
        newEnd = new Date(dragState.origEnd.getTime() + daysDelta * DAY_MS);
      }

      setDragPreview({ start: newStart, end: newEnd });
    },
    [dragState, dayWidth]
  );

  const handleMouseUp = useCallback(() => {
    if (!dragState || !dragPreview) {
      setDragState(null);
      setDragPreview(null);
      return;
    }

    const origStartStr = formatYMD(dragState.origStart);
    const origEndStr = formatYMD(dragState.origEnd);
    const newStartStr = formatYMD(dragPreview.start);
    const newEndStr = formatYMD(dragPreview.end);

    if (newStartStr !== origStartStr || newEndStr !== origEndStr) {
      onUpdatePhase(dragState.phaseId, {
        planned_start: newStartStr,
        planned_end: newEndStr,
      });
    }

    setDragState(null);
    setDragPreview(null);
  }, [dragState, dragPreview, onUpdatePhase]);

  const sortedPhases = useMemo(
    () => [...phases].sort((a, b) => a.sort_order - b.sort_order),
    [phases]
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t('phases.timelinePlanner')}
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {t('phases.dragToAdjust')}
        </span>
      </div>

      <div
        ref={containerRef}
        className="overflow-x-auto"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div style={{ minWidth: chartWidth + LABEL_WIDTH }} className="relative">
          {/* Month headers */}
          <div className="flex border-b border-gray-200 dark:border-gray-700" style={{ height: 28, marginLeft: LABEL_WIDTH }}>
            {months.map((m, i) => (
              <div
                key={i}
                className="text-xs text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 px-1 truncate"
                style={{
                  position: 'absolute',
                  left: `calc(${LABEL_WIDTH}px + ${m.left}%)`,
                  width: `${m.width}%`,
                  lineHeight: '28px',
                }}
              >
                {m.label}
              </div>
            ))}
          </div>

          {/* Phase rows */}
          {sortedPhases.map((phase) => {
            const start = parseDate(phase.planned_start);
            const end = parseDate(phase.planned_end);
            const hasDates = start && end;
            const colors = PHASE_TYPE_COLORS[phase.phase_type] || PHASE_TYPE_COLORS.custom;

            // Use drag preview if this phase is being dragged
            const displayStart = dragState?.phaseId === phase.id && dragPreview ? dragPreview.start : start;
            const displayEnd = dragState?.phaseId === phase.id && dragPreview ? dragPreview.end : end;

            const barLeft = hasDates && displayStart ? (diffDays(rangeStart, displayStart) / totalDays) * 100 : 0;
            const barWidth = hasDates && displayStart && displayEnd ? (diffDays(displayStart, displayEnd) / totalDays) * 100 : 0;

            return (
              <div
                key={phase.id}
                className="flex items-center border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                style={{ height: ROW_HEIGHT }}
              >
                {/* Label */}
                <div
                  className="flex items-center gap-1.5 px-3 text-xs font-medium text-gray-700 dark:text-gray-300 shrink-0 truncate"
                  style={{ width: LABEL_WIDTH }}
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: colors.dark }}
                  />
                  <span className="truncate">{phase.phase_name}</span>
                </div>

                {/* Chart area */}
                <div className="relative flex-1" style={{ height: ROW_HEIGHT }}>
                  {hasDates ? (
                    <div
                      className="absolute top-1.5 rounded group"
                      style={{
                        left: `${barLeft}%`,
                        width: `${Math.max(barWidth, 0.5)}%`,
                        height: ROW_HEIGHT - 12,
                        backgroundColor: colors.light,
                        border: `1px solid ${colors.dark}`,
                        cursor: dragState ? 'grabbing' : 'grab',
                      }}
                      onMouseDown={(e) => handleMouseDown(e, phase.id, 'body', start!, end!)}
                    >
                      {/* Left handle */}
                      <div
                        className="absolute top-0 left-0 h-full opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ width: HANDLE_WIDTH, cursor: 'col-resize' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleMouseDown(e, phase.id, 'left', start!, end!);
                        }}
                      >
                        <div className="w-1 h-3 bg-gray-600 dark:bg-gray-300 rounded absolute top-1/2 left-1 -translate-y-1/2" />
                      </div>

                      {/* Bar label */}
                      <div className="px-2 h-full flex items-center overflow-hidden">
                        <span className="text-[10px] font-medium truncate" style={{ color: colors.dark }}>
                          {PHASE_TYPE_LABELS[phase.phase_type]}
                        </span>
                        <span className={`ml-1.5 px-1 py-0.5 text-[9px] rounded ${PHASE_STATUS_COLORS[phase.status]}`}>
                          {PHASE_STATUS_LABELS[phase.status]}
                        </span>
                      </div>

                      {/* Right handle */}
                      <div
                        className="absolute top-0 right-0 h-full opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ width: HANDLE_WIDTH, cursor: 'col-resize' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleMouseDown(e, phase.id, 'right', start!, end!);
                        }}
                      >
                        <div className="w-1 h-3 bg-gray-600 dark:bg-gray-300 rounded absolute top-1/2 right-1 -translate-y-1/2" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center h-full px-4">
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                        {t('phases.noDatesSet')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Today marker */}
          {todayPos !== null && (
            <div
              className="absolute top-0 bottom-0 w-px bg-red-500 z-10 pointer-events-none"
              style={{ left: `calc(${LABEL_WIDTH}px + ${todayPos}%)` }}
            >
              <div className="absolute -top-0 -translate-x-1/2 text-[9px] bg-red-500 text-white px-1 rounded-b">
                {t('phases.today')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhaseTimelinePlanner;
